import {
  AutoLinkPlugin,
  createLinkMatcherWithRegExp,
  LinkMatcher,
} from '@lexical/react/LexicalAutoLinkPlugin';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {EditorRefPlugin} from '@lexical/react/LexicalEditorRefPlugin';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {$getRoot, $getSelection, $setSelection, EditorState, LexicalEditor} from 'lexical';
import {BeautifulMentionsPlugin} from 'lexical-beautiful-mentions';
import {Show, Spinner} from 'platform/foundation';

import {useRef} from 'react';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, useDeepCompareEffect, useLatest} from 'shared/web';

import {OptionStatusIndicator} from '../../types/OptionTypeBase';
import {CustomMenuItem} from '../Dropdown/components/CustomMenuItem';
import {MenuItemStatusIndicatorContext} from '../Dropdown/components/MenuItemStatusIndicatorContext';
import {Dropdown} from '../Dropdown/Dropdown';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {FooterBar, FooterContent} from '../RichText/components/FooterBar';
import {TextEditorFrame} from '../TextEditorFrame/TextEditorFrame';
import {TextEditorOverlays} from '../TextEditorFrame/TextEditorOverlays';
import {Placeholder} from './components/Placeholder';
import {StyledContentEditable} from './components/StyledContentEditable';
import {EditorBridgePlugin} from './plugins/EditorBridgePlugin';
import {NoParagraphPlugin} from './plugins/NoParagraphPlugin';
import {OpenLinkPlugin} from './plugins/OpenLinkPlugin';
import {RawPastePlugin} from './plugins/RawPastePlugin';
import {SuggestionInputProps} from './types/SuggestionInputProps';
import {getEditorConfig} from './utils/getEditorConfig';
import {EMAIL_REGEX, URL_REGEX} from './utils/getFormattedValue';
import {$serializeEditorHtml} from './utils/serializeEditorHtml';

const EMPTY_STATUS_INDICATORS: Map<string, OptionStatusIndicator> = new Map();

const MATCHERS: Array<LinkMatcher> = [
  createLinkMatcherWithRegExp(URL_REGEX, (text) =>
    text.startsWith('http') ? text : `https://${text}`
  ),
  createLinkMatcherWithRegExp(EMAIL_REGEX, (text) => `mailto:${text}`),
];

export function SuggestionInput(props: SuggestionInputProps) {
  const editor = useRef<LexicalEditor>(null);
  const lastState = useRef<string>(null);
  const transformStringToNodes = useLatest(props.transformStringToNodes);

  const ContentEditable = props.ContentEditableComponent ?? StyledContentEditable;
  const MenuItem = props.MenuItemComponent ?? CustomMenuItem;

  const onChange = (state: EditorState, editor: LexicalEditor) => {
    state.read(() => {
      lastState.current = $serializeEditorHtml(editor);
      props.onChange?.(lastState.current);
    });
  };

  useDeepCompareEffect(
    function rehydrateEditorFromValue() {
      // Rehydrating before the options arrive would drop every mention (their labels are unknown yet)
      // and, through onChange, write that stripped text back into the form value. Wait for them.
      if (props.isLoadingOptions || props.value === lastState.current) {
        return;
      }

      editor.current?.update(() => {
        $getRoot().clear();
        $getRoot().select();
        $getSelection()?.insertNodes(transformStringToNodes.current(props.value));
        $setSelection(null);
      });
    },
    [props.value, props.isLoadingOptions]
  );

  return (
    <MenuItemStatusIndicatorContext.Provider
      value={props.statusIndicatorById ?? EMPTY_STATUS_INDICATORS}
    >
      <div>
        <Label
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          actions={props.labelActions}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
        <TextEditorFrame
          onKeyDownCapture={props.onKeyDownCapture}
          $isBorderless={isNil(props.editorContent)}
          $isDisabled={props.isDisabled}
          $isInvalid={props.isInvalid || isNotNil(props.errorMessage)}
        >
          <Show when={props.isLoadingOptions}>
            <Spinner variant="overlay" />
          </Show>
          <LexicalComposer initialConfig={getEditorConfig(props)}>
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  $isDisabled={props.isDisabled}
                  $isInvalid={props.isInvalid || isNotNil(props.errorMessage)}
                  $minHeight={props.minHeight}
                  $isFramed={isNotNil(props.editorContent)}
                  aria-label={props.label ?? undefined}
                  data-testid={props['data-testid']}
                />
              }
              placeholder={
                <Show when={props.placeholder}>
                  <Placeholder>{props.placeholder}</Placeholder>
                </Show>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <EditorRefPlugin editorRef={editor} />
            <LinkPlugin />
            <RawPastePlugin />
            <NoParagraphPlugin />
            <OpenLinkPlugin />
            <OnChangePlugin onChange={onChange} ignoreSelectionChange />
            <Show when={props.onEditorReady}>
              <EditorBridgePlugin
                onEditorReady={props.onEditorReady}
                transformStringToNodes={props.transformStringToNodes}
                options={props.options}
                isLoadingOptions={props.isLoadingOptions}
              />
            </Show>
            <BeautifulMentionsPlugin
              items={{
                [props.prefix]: props.options,
              }}
              menuItemComponent={MenuItem}
              menuItemLimit={false}
              menuComponent={({children}) => (
                <Dropdown
                  isOpen
                  dropdownControl={<div />}
                  data-testid={suffixTestId('dropdown', props)}
                  isFocusManagementDisabled
                  isHeightLimited
                >
                  <ul>{children}</ul>
                </Dropdown>
              )}
              showMentionsOnDelete
            />

            <AutoLinkPlugin matchers={MATCHERS} />
          </LexicalComposer>
          <Show
            when={
              !props.isDisabled &&
              (isNotNil(props.selectionContent) || isNotNil(props.editorHighlights))
            }
          >
            <TextEditorOverlays
              getEditor={() => editor.current?.getRootElement() ?? null}
              label={props.label}
              selectionContent={props.selectionContent}
              editorHighlights={props.editorHighlights}
            />
          </Show>
          <Show when={isNotNil(props.footerContent)}>
            <FooterBar>
              <FooterContent>{props.footerContent}</FooterContent>
            </FooterBar>
          </Show>
          {props.editorContent}
        </TextEditorFrame>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </div>
    </MenuItemStatusIndicatorContext.Provider>
  );
}
